import './OfferRate.scss';
import { useEffect, useMemo, useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import Modal from '@srm/ui/modal';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IOption } from '@srm/ui/types/IOption';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import Hint from '@srm/ui/hint';
import { IOfferRateFormData } from './IOfferRateFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import SelectControl from '@srm/ui/controls/SelectControl';

export interface IOfferRateProps {
    tableKey: string;
    isEdit: boolean;
    value: IOffer;
    demandId: string;
    onSubmit?: (value: IOfferRateFormData) => void;
    onClose?: () => void;
    offerUnAcceptableHint?: string;
    lowCostPurchase?: boolean;
}

const OfferRate = ({
    tableKey,
    isEdit,
    value,
    demandId,
    onSubmit,
    onClose,
    offerUnAcceptableHint,
    lowCostPurchase = false,
}: IOfferRateProps) => {
    const [error, setError] = useState<string>();
    const methods = useForm<IOfferRateFormData>({
        mode: 'onBlur',
    });
    const hasUnsavedInput = useUnsavedFormInput(methods);

    const { isSubmitting, isDirty } = methods.formState;

    const watchRank = useWatch({ name: 'rank', control: methods.control });

    useEffect(() => {
        const defaultValue: IOfferRateFormData = {
            result: value.result,
            rank: value.rank,
        };
        methods.reset(defaultValue);
    }, [value]);

    useEffect(() => {
        if (isDirty) {
            methods.setValue('result', null, { shouldDirty: true });
        }
    }, [watchRank]);

    const onFormSubmit = async (form: IOfferRateFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferRate(value.key, demandId, tableKey, form)
            .then(() => {
                sendNotification({
                    message: 'Данные успешно сохранены',
                    variant: 'green',
                });
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const ranks: IOption[] = useMemo(() => {
        const base: IOption[] = [{ label: '', value: null } as unknown as IOption];
        return base.concat(
            [...Array(10).keys()].map((val) => {
                return { label: `${val + 1}`, value: `${val + 1}` } as IOption;
            }),
        );
    }, []);

    const resultsRankOptions: IOption[] = useMemo(() => {
        return watchRank === '1'
            ? [
                  { label: '', value: null },
                  { label: 'Основной', value: 'Основной' },
              ]
            : value.acceptanceResult !== 1
              ? [
                    { label: '', value: null },
                    { label: 'Одобренный после теста', value: 'Одобренный после теста' },
                ]
              : [
                    { label: '', value: null },
                    { label: 'Резервный', value: 'Резервный' },
                    { label: 'Одобренный после теста', value: 'Одобренный после теста' },
                ];
    }, [value.acceptanceResult, watchRank]);

    const resultVisible =
        (watchRank === '1' && value.acceptanceResult === 1) || (watchRank === '2' && lowCostPurchase === false);

    const errorLabel = error && (
        <div className="offer-rate__error">
            <Hint role="alert" title={error} tone="danger" />
        </div>
    );

    const offerUnAcceptableLabel = value.acceptanceResult !== 1 && offerUnAcceptableHint && (
        <div className={'offer-crit-details__hint'}>
            <Hint role="alert" title={offerUnAcceptableHint} tone="warning" maxWidth="100%" />
        </div>
    );

    const modalFooter = (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-rate-form"
                loading={isSubmitting}
                className={clsx('offer-rate__button', 'offer-rate__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                Сохранить
            </Button>
        </div>
    );

    return (
        <Modal
            size="m"
            onClose={onClose}
            hasUnsavedInput={() => isEdit && hasUnsavedInput()}
            header={`${value.name} (предложение №${value.number || ''})`}
            footer={isEdit ? modalFooter : null}
        >
            {errorLabel}
            {offerUnAcceptableLabel}
            <FormProviderControl
                formMethods={methods}
                id="offer-rate-form"
                className="offer-rate__form"
                onSubmit={onFormSubmit}
            >
                <HookFormProvider controller={Controller}>
                    <div className="offer-rate__content">
                        <div className="offer-rate__row">
                            <SelectControl
                                label="Место"
                                name="rank"
                                readOnly={true}
                                disabled={!isEdit}
                                highlightMenuItems={false}
                                options={ranks}
                                control={methods.control}
                                setValue={(options) => {
                                    return options[0].value;
                                }}
                                getValue={(options: IOption[], value: any) => {
                                    return options.filter((x) => x.value === value);
                                }}
                            />
                            {resultVisible ? (
                                <SelectControl
                                    label="Результат"
                                    name="result"
                                    readOnly={true}
                                    disabled={!isEdit}
                                    highlightMenuItems={false}
                                    options={resultsRankOptions}
                                    control={methods.control}
                                    setValue={(options) => {
                                        return options[0].value;
                                    }}
                                    getValue={(options: IOption[], value: any) => {
                                        return options.filter((x) => x.value === value);
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default OfferRate;
